import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  ApiError,
  uploadUrl,
  type AccessMode,
  type JoinCode,
  type Knock,
  type Member,
  type Room as RoomType,
  type User,
} from "../lib/api";
import { applyTheme, THEMES, themeFrom, type ThemeName } from "../lib/themes";
import { useLiveRoom, type DeviceKind, type Devices } from "../lib/useLiveRoom";
import { useRoomChannel, type RoomObject, type Shape } from "../lib/useRoomChannel";
import { Canvas, STROKE_COLORS } from "../components/Canvas";
import { Icon, type IconName } from "../components/Icon";
import { IMAGE_ACCEPT, toUploadableImage } from "../lib/image";
import { startKeepAlive } from "../lib/keepAlive";

type Tab = "chat" | "people" | "requests" | "settings";

const BACKDROPS = [
  "plain",
  "grid",
  "dots",
  "sunset",
  "nightsky",
  "paper",
  "stage",
  "garden",
] as const;

const REACTIONS = ["❤️", "😂", "🔥", "👏", "🎉", "😮", "👀", "💯", "✨"];

/**
 * Closes a popover when the next press lands outside it, or on Escape.
 *
 * Registered on the capture phase: the room's canvas stops pointer events
 * propagating so that a drag does not also count as a click on the page, and a
 * listener waiting for the bubble phase would simply never hear a press that
 * landed on the room. Attaching it while open only, so a page with no menu
 * showing does no work per press.
 */
function useDismiss<T extends HTMLElement>(open: boolean, close: () => void) {
  const ref = useRef<T | null>(null);
  const closeRef = useRef(close);
  closeRef.current = close;

  useEffect(() => {
    if (!open) return;

    const onPointerDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) closeRef.current();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeRef.current();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return ref;
}

/**
 * What to tell someone when an upload fails. A conversion failure carries its
 * own message worth reading — "export it as a JPEG" — so it is not flattened
 * into the generic one.
 */
function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

export function Room({
  roomId,
  user,
  onLeave,
}: {
  roomId: string;
  user: User;
  onLeave: () => void;
}) {
  const [room, setRoom] = useState<RoomType | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [knocks, setKnocks] = useState<Knock[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>("chat");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [drawMode, setDrawMode] = useState(false);
  const [drawColor, setDrawColor] = useState("chalk");
  const [drawWidth, setDrawWidth] = useState(4);
  const [uploading, setUploading] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const live = useLiveRoom(room ? roomId : null, `u_${user.id}`);
  const scene = useRoomChannel(room ? roomId : null);
  const canModerate = room?.role === "host" || room?.role === "moderator";

  const refresh = useCallback(async () => {
    const { room, members } = await api.getRoom(roomId);
    setRoom(room);
    setMembers(members);
    return room;
  }, [roomId]);

  useEffect(() => {
    refresh().catch((err) =>
      setLoadError(
        err instanceof ApiError && err.status === 404
          ? "This room doesn't exist, or you're not in it."
          : "Couldn't load the room.",
      ),
    );
  }, [refresh]);

  // Being in a room is mostly websocket traffic, which can leave the API with
  // no HTTP request for long enough that its host puts it to sleep underneath
  // a session that is still open. Only runs while a room is actually on screen.
  useEffect(startKeepAlive, []);

  useEffect(() => {
    if (rootRef.current && room) applyTheme(rootRef.current, themeFrom(room.theme));
  }, [room]);

  // Presence tells us who is here; the roster comes from the API, because it
  // also carries roles and the people who are members but not currently in.
  // So an arrival nobody has heard of is the cue to re-read it — otherwise a
  // new person stayed invisible in People until someone reloaded the page.
  const presentKey = useMemo(
    () => scene.present.map((p) => p.user_id).sort().join(","),
    [scene.present],
  );

  // Read through a ref, not a dependency: making `members` a dependency would
  // re-run this on the very state it sets.
  const membersRef = useRef<Member[]>([]);
  membersRef.current = members;

  useEffect(() => {
    const known = new Set(membersRef.current.map((m) => m.user.id));
    if (scene.present.every((p) => known.has(p.user_id))) return;

    let alive = true;
    api
      .getRoom(roomId)
      .then(({ members }) => alive && setMembers(members))
      .catch(() => {});
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentKey, roomId]);

  useEffect(() => {
    if (!focusMode) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setFocusMode(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [focusMode]);

  /**
   * Reveals the chrome while the pointer is near the top or bottom of the
   * window.
   *
   * Position-based rather than hover-based on purpose. Hover cannot work here:
   * the revealed toolbar sits over whatever triggered it, so the trigger
   * immediately reports the pointer leaving and the chrome flickers. Asking
   * where the pointer actually is has no such feedback loop — the bar stays up
   * precisely because your cursor is on it.
   *
   * The band is deeper than the bars themselves, so the chrome does not snap
   * away the moment you drift a pixel off it.
   */
  const onRoomPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!focusMode) return;
      const y = e.clientY;
      const height = window.innerHeight;
      setRevealed(y < 110 || y > height - 150);
    },
    [focusMode],
  );

  // Leaving focus mode should not strand the chrome in a revealed state.
  useEffect(() => {
    if (!focusMode) setRevealed(false);
  }, [focusMode]);

  // Knocks still poll: they arrive over HTTP, not the room channel. Moving
  // them onto the channel is worth doing, but the room layout was the part
  // that could not tolerate a 5-second delay.
  useEffect(() => {
    if (!canModerate || room?.access_mode !== "closed") return;
    let alive = true;
    const poll = () =>
      api
        .listKnocks(roomId)
        .then(({ knocks }) => alive && setKnocks(knocks))
        .catch(() => {});
    poll();
    const timer = setInterval(poll, 5000);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [canModerate, room?.access_mode, roomId]);

  // Index LiveKit tracks by the key the canvas asks for, so a camera frame can
  // find the live video belonging to its subject.
  const tilesByIdentity = useMemo(() => {
    const map = new Map<string, (typeof live.tiles)[number]>();
    for (const tile of live.tiles) map.set(tile.key, tile);
    return map;
  }, [live.tiles]);

  const nameByUserId = useMemo(() => {
    const map = new Map<number, string>();
    for (const m of members) map.set(m.user.id, m.user.display_name);
    for (const p of scene.present) map.set(p.user_id, p.display_name);
    return map;
  }, [members, scene.present]);

  const objects = useMemo(
    () => Object.values(scene.objects).sort((a, b) => a.z - b.z),
    [scene.objects],
  );

  const selected = selectedId ? scene.objects[selectedId] : null;
  const backdrop = objects.find((o) => o.kind === "backdrop");

  // Shown on the picker's own chip, so the photo option previews the photo
  // the room is actually wearing.
  const backdropPhotoUrl =
    backdrop?.data?.preset === "photo" && backdrop.data?.upload_id
      ? uploadUrl(String(backdrop.data.upload_id))
      : null;

  // Where the room put this person's own camera frame. Handed to the canvas so
  // the first thing they see on arriving is themselves — on a phone the room is
  // several screens wide and their frame is very unlikely to be in the part
  // that happens to be showing.
  const myFrame = useMemo(() => {
    const mine = objects.find(
      (o) => o.kind === "camera" && o.subject_user_id === user.id,
    );
    return mine ? { x: mine.x + mine.width / 2, y: mine.y + mine.height / 2 } : null;
  }, [objects, user.id]);

  const addNote = () => {
    const text = prompt("What should the note say?");
    if (!text?.trim()) return;
    scene.createObject({
      kind: "note",
      x: 200 + Math.random() * 400,
      y: 200 + Math.random() * 300,
      width: 260,
      height: 200,
      z: 20,
      shape: "rect",
      data: { text: text.slice(0, 2000), color: "yellow" },
    });
  };

  async function pickImage(file: File) {
    setUploading(true);
    try {
      const { upload } = await api.uploadImage(roomId, await toUploadableImage(file));
      // The object stores only the upload id. A URL would let a room pull
      // images from anywhere and leak every visitor's IP to that host.
      scene.createObject({
        kind: "image",
        x: 240 + Math.random() * 300,
        y: 200 + Math.random() * 200,
        width: 420,
        height: 300,
        z: 15,
        shape: "rounded",
        data: { upload_id: upload.id, alt: file.name.slice(0, 200) },
      });
    } catch (err) {
      alert(errorText(err, "Couldn't add that image."));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  // `uploadId` is set only for the photo backdrop; a pattern carries nothing
  // but its name, and the server drops any id sent alongside one.
  const setBackdrop = (preset: string, uploadId?: string) => {
    const data = uploadId ? { preset, upload_id: uploadId } : { preset };

    if (backdrop) {
      scene.placeObject({ id: backdrop.id, data });
    } else {
      scene.createObject({
        kind: "backdrop",
        x: 0,
        y: 0,
        // Matches the canvas, so the backdrop covers the whole room.
        width: 1920,
        height: 1080,
        z: -100,
        shape: "rect",
        data,
      });
    }
  };

  // The backdrop photo goes through the same upload path as any other image —
  // held in memory, fetched by id, never a URL the room supplies.
  async function pickBackdropPhoto(file: File) {
    setUploading(true);
    try {
      const { upload } = await api.uploadImage(roomId, await toUploadableImage(file));
      setBackdrop("photo", upload.id);
    } catch (err) {
      alert(errorText(err, "Couldn't use that photo."));
    } finally {
      setUploading(false);
    }
  }

  if (loadError) {
    return (
      <div className="auth-wrap">
        <div className="card auth-card" style={{ textAlign: "center" }}>
          <h1 style={{ fontSize: 20 }}>{loadError}</h1>
          <button className="btn primary" onClick={onLeave} style={{ marginTop: 14 }}>
            Back to your rooms
          </button>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="auth-wrap">
        <span className="spinner" />
      </div>
    );
  }

  return (
    <div
      className={`room${focusMode ? " focused" : ""}${revealed ? " revealed" : ""}`}
      ref={rootRef}
      onPointerMove={onRoomPointerMove}
      onPointerLeave={() => setRevealed(false)}
    >
      <header className="topbar">
        <div className="row" style={{ minWidth: 0 }}>
          <button className="brand" onClick={onLeave} title="Back to your rooms">
            <span className="dot" />
          </button>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontWeight: 650, overflow: "hidden", textOverflow: "ellipsis" }}>
              {room.name}
            </div>
            <div className="hint" style={{ fontSize: 12 }}>
              {scene.present.length} here · {room.access_mode === "open" ? "Open" : "Ask to join"}
            </div>
          </div>
        </div>

        <div className="row">
          {canModerate && knocks.length > 0 && (
            <button
              className="btn small"
              onClick={() => {
                setTab("requests");
                setSidebarOpen(true);
              }}
            >
              {knocks.length} waiting
            </button>
          )}
          {canModerate && (
            <button
              className="btn ghost small"
              onClick={() => {
                setTab("settings");
                setSidebarOpen(true);
              }}
              title="Show the code for getting in here"
            >
              <Icon name="link" size={16} />
              Invite
            </button>
          )}
          <button
            className={`btn ghost small${sidebarOpen ? " on" : ""}`}
            onClick={() => setSidebarOpen((o) => !o)}
            title={sidebarOpen ? "Hide the panel" : "Show chat and people"}
          >
            <Icon name="panel" size={16} />
            Panel
          </button>
          <button
            className={`btn ghost small${focusMode ? " on" : ""}`}
            onClick={() => setFocusMode((f) => !f)}
            title={
              focusMode ? "Show the controls again (Esc)" : "Hide everything but the room"
            }
          >
            <Icon name={focusMode ? "collapse" : "expand"} size={16} />
            Focus
          </button>
          <button className="btn ghost small" onClick={onLeave} title="Leave the room">
            <Icon name="leave" size={16} />
            Leave
          </button>
        </div>
      </header>

      {live.error && <div className="banner">{live.error}</div>}
      {scene.error && <div className="banner">{scene.error}</div>}

      <div className="room-main">
        <Canvas
          objects={objects}
          tilesByIdentity={tilesByIdentity}
          nameByUserId={nameByUserId}
          myUserId={user.id}
          selectedId={selectedId}
          drawMode={drawMode}
          drawColor={drawColor}
          drawWidth={drawWidth}
          focusOn={myFrame}
          onSelect={setSelectedId}
          onMove={scene.moveObject}
          onPlace={scene.placeObject}
          onDrawn={(strokes, bounds) =>
            scene.createObject({
              kind: "drawing",
              x: bounds.x,
              y: bounds.y,
              width: bounds.w,
              height: bounds.h,
              z: 25,
              shape: "rect",
              data: { strokes, color: drawColor, width: drawWidth },
            })
          }
        />

        {selected && selected.kind !== "backdrop" && (
          <SelectedTools
            object={selected}
            canDelete={
              selected.kind !== "camera" &&
              selected.kind !== "screen" &&
              (selected.created_by_id === user.id || canModerate)
            }
            onShape={(shape) => scene.placeObject({ id: selected.id, shape })}
            onLayer={(direction) => {
              // Only things you can actually see count. A screen frame exists
              // even when nobody is sharing, and ordering against those
              // invisible frames is what made these buttons look broken:
              // "bring to front" would put an object above a frame that was
              // not on screen and leave the visible stack exactly as it was.
              const visible = objects.filter(
                (o) =>
                  o.id !== selected.id &&
                  o.kind !== "backdrop" &&
                  (o.kind !== "screen" ||
                    tilesByIdentity.has(`u_${o.subject_user_id}:screen`)),
              );
              const zs = visible.map((o) => o.z);

              // The object's own z is part of the comparison. Measuring only
              // against everything else meant "bring to front" could hand it a
              // *lower* z than it already had — the second half of the same bug.
              const z =
                direction === "front"
                  ? Math.min(1000, Math.max(selected.z, ...zs) + 1)
                  : Math.max(-99, Math.min(selected.z, ...zs) - 1);
              scene.placeObject({ id: selected.id, z });
            }}
            onDelete={() => {
              scene.deleteObject(selected.id);
              setSelectedId(null);
            }}
          />
        )}

        {scene.reactions.length > 0 && (
          <div className="reaction-float">
            {scene.reactions.map((r) => (
              <span key={r.id}>{r.emoji}</span>
            ))}
          </div>
        )}

        {focusMode && (
          <>
            <button
              className="btn icon focus-exit"
              onClick={() => setFocusMode(false)}
              title="Show the controls again (Esc)"
              aria-label="Leave focus mode"
            >
              <Icon name="collapse" />
            </button>
          </>
        )}

        <aside className={`sidebar${sidebarOpen ? " open" : ""}`}>
          <div className="tabs">
            <button className={tab === "chat" ? "active" : ""} onClick={() => setTab("chat")}>
              Chat
            </button>
            <button className={tab === "people" ? "active" : ""} onClick={() => setTab("people")}>
              People
            </button>
            {canModerate && room.access_mode === "closed" && (
              <button
                className={tab === "requests" ? "active" : ""}
                onClick={() => setTab("requests")}
              >
                Asks{knocks.length > 0 && <span className="pip">{knocks.length}</span>}
              </button>
            )}
            {canModerate && (
              <button
                className={tab === "settings" ? "active" : ""}
                onClick={() => setTab("settings")}
              >
                Room
              </button>
            )}
          </div>

          {tab === "chat" && <Chat live={live} />}
          {tab === "people" && (
            <People
              members={members}
              room={room}
              me={user}
              presentIds={new Set(scene.present.map((p) => p.user_id))}
              onChanged={refresh}
              setMembers={setMembers}
            />
          )}
          {tab === "requests" && canModerate && (
            <Requests knocks={knocks} setKnocks={setKnocks} onApproved={refresh} />
          )}
          {tab === "settings" && canModerate && <Settings room={room} setRoom={setRoom} />}
        </aside>
      </div>

      <div className="palette">
        <div className="tool-group">
          <MediaControl
            on={live.micOn}
            onToggle={live.toggleMic}
            icon={live.micOn ? "mic" : "micOff"}
            label={live.micOn ? "Mute yourself" : "Unmute yourself"}
            chooseLabel="Choose your microphone"
            groups={[
              { kind: "audioinput", label: "Microphone" },
              { kind: "audiooutput", label: "Speaker" },
            ]}
            devices={live.devices}
            onSelect={live.selectDevice}
          />
          <MediaControl
            on={live.cameraOn}
            onToggle={live.toggleCamera}
            icon={live.cameraOn ? "camera" : "cameraOff"}
            label={live.cameraOn ? "Turn camera off" : "Turn camera on"}
            chooseLabel="Choose your camera"
            groups={[{ kind: "videoinput", label: "Camera" }]}
            devices={live.devices}
            onSelect={live.selectDevice}
          />
          <button
            className={`btn ${live.screenOn ? "on" : ""}`}
            onClick={live.toggleScreen}
            title="Put your screen in the room"
          >
            <Icon name="screen" />
            {live.screenOn ? "Stop sharing" : "Share screen"}
          </button>
        </div>

        <span className="divider" />

        <div className="tool-group">
          <button className="btn" onClick={addNote} title="Stick a note on the wall">
            <Icon name="note" />
            Note
          </button>

          <button
            className="btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            title="Put a photo in the room"
          >
            {uploading ? <span className="spinner" /> : <Icon name="image" />}
            Photo
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) pickImage(file);
            }}
          />

          <button
            className={`btn ${drawMode ? "on" : ""}`}
            onClick={() => setDrawMode((d) => !d)}
            title="Scribble on the room"
          >
            <Icon name="brush" />
            {drawMode ? "Done" : "Draw"}
          </button>

          <BackdropPicker
            current={backdrop?.data?.preset ?? "plain"}
            photoUrl={backdropPhotoUrl}
            busy={uploading}
            onPick={setBackdrop}
            onPickPhoto={pickBackdropPhoto}
          />
          <ReactionPicker onReact={scene.react} />
        </div>
      </div>

      {drawMode && (
        <div className="pen-bar">
          <span className="hint">Pen</span>
          {Object.entries(STROKE_COLORS).map(([key, hex]) => (
            <button
              key={key}
              className={`ink-chip${drawColor === key ? " active" : ""}`}
              style={{ background: hex }}
              title={key}
              aria-label={`${key} ink`}
              onClick={() => setDrawColor(key)}
            />
          ))}
          <span className="divider" />
          {[2, 4, 8, 14].map((w) => (
            <button
              key={w}
              className={`btn icon small${drawWidth === w ? " on" : ""}`}
              onClick={() => setDrawWidth(w)}
              title={`${w}px`}
              aria-label={`Stroke width ${w}`}
            >
              <span
                style={{
                  width: w + 2,
                  height: w + 2,
                  borderRadius: "50%",
                  background: "currentColor",
                  display: "block",
                }}
              />
            </button>
          ))}
        </div>
      )}

    </div>
  );
}

/* ------------------------------------------------- selected-object toolbar */

function SelectedTools({
  object,
  canDelete,
  onShape,
  onLayer,
  onDelete,
}: {
  object: RoomObject;
  canDelete: boolean;
  onShape: (s: Shape) => void;
  onLayer: (direction: "front" | "back") => void;
  onDelete: () => void;
}) {
  const shapes: { key: Shape; icon: "square" | "rounded" | "circle" }[] = [
    { key: "rect", icon: "square" },
    { key: "rounded", icon: "rounded" },
    { key: "circle", icon: "circle" },
  ];

  return (
    <div className="selected-tools">
      {shapes.map((s) => (
        <button
          key={s.key}
          className={`btn small${object.shape === s.key ? " on" : ""}`}
          onClick={() => onShape(s.key)}
          title={`Make it ${s.key}`}
          aria-label={`Make it ${s.key}`}
        >
          <Icon name={s.icon} size={16} />
        </button>
      ))}
      <span className="divider" />
      <button
        className="btn small"
        onClick={() => onLayer("front")}
        title="Bring to front"
        aria-label="Bring to front"
      >
        <Icon name="bringForward" size={16} />
      </button>
      <button
        className="btn small"
        onClick={() => onLayer("back")}
        title="Send to back"
        aria-label="Send to back"
      >
        <Icon name="sendBack" size={16} />
      </button>
      {canDelete && (
        <>
          <span className="divider" />
          <button className="btn small danger" onClick={onDelete} title="Remove" aria-label="Remove">
            <Icon name="trash" size={16} />
          </button>
        </>
      )}
    </div>
  );
}

function BackdropPicker({
  current,
  photoUrl,
  busy,
  onPick,
  onPickPhoto,
}: {
  current: string;
  photoUrl: string | null;
  busy: boolean;
  onPick: (preset: string) => void;
  onPickPhoto: (file: File) => void;
}) {
  const [open, setOpen] = useState(false);
  const photoRef = useRef<HTMLInputElement | null>(null);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  return (
    <div className="popover-anchor" ref={ref}>
      <button className="btn" onClick={() => setOpen((o) => !o)} title="Change the backdrop">
        <Icon name="backdrop" />
        Backdrop
      </button>
      {open && (
        <div className="card popover backdrop-menu">
          <div className="backdrop-row">
            {BACKDROPS.map((preset) => (
              <button
                key={preset}
                className={`backdrop-chip${current === preset ? " active" : ""}`}
                title={preset}
                onClick={() => {
                  onPick(preset);
                  setOpen(false);
                }}
              >
                <span className={`backdrop-${preset}`} />
              </button>
            ))}

            {/* The photo sits with the patterns because it is one more thing
                the wall can be — and shows the photo itself once there is one. */}
            <button
              className={`backdrop-chip${current === "photo" ? " active" : ""}`}
              title="A photo from your device"
              disabled={busy}
              onClick={() => photoRef.current?.click()}
            >
              {photoUrl ? (
                <span style={{ background: `url(${photoUrl}) center/cover` }} />
              ) : (
                <span className="backdrop-upload">
                  {busy ? <span className="spinner" /> : <Icon name="image" size={16} />}
                </span>
              )}
            </button>
          </div>

          <input
            ref={photoRef}
            type="file"
            accept={IMAGE_ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = "";
              if (file) {
                onPickPhoto(file);
                setOpen(false);
              }
            }}
          />
        </div>
      )}
    </div>
  );
}

/**
 * A media button with its own little arrow, the way a call app does it: press
 * the button to mute or unmute, press the arrow to choose which device it
 * uses. The arrow is the whole point — switching your microphone used to mean
 * finding a separate control that had nothing to do with the mic button.
 */
function MediaControl({
  on,
  onToggle,
  icon,
  label,
  chooseLabel,
  groups,
  devices,
  onSelect,
}: {
  on: boolean;
  onToggle: () => void;
  icon: IconName;
  label: string;
  chooseLabel: string;
  groups: { kind: DeviceKind; label: string }[];
  devices: Devices;
  onSelect: (kind: DeviceKind, deviceId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  // Firefox and iOS cannot redirect audio output and report no such devices;
  // that group is dropped rather than shown as an empty heading.
  const shown = groups.filter((group) => devices.list[group.kind].length > 0);

  return (
    <div className="media-control" ref={ref}>
      <button
        className={`btn icon media-main ${on ? "on" : "off"}`}
        onClick={onToggle}
        title={label}
        aria-label={label}
      >
        <Icon name={icon} />
      </button>
      <button
        className="btn media-caret"
        onClick={() => setOpen((o) => !o)}
        title={chooseLabel}
        aria-label={chooseLabel}
        aria-expanded={open}
      >
        <Icon name="caret" size={14} />
      </button>

      {open && (
        <div className="card popover device-menu">
          {shown.length === 0 && (
            <p className="hint" style={{ margin: 0 }}>
              Nothing to choose from yet — allow the camera and mic first.
            </p>
          )}
          {shown.map((group) => (
            <div key={group.kind} className="device-group">
              <div className="device-heading">{group.label}</div>
              {devices.list[group.kind].map((device, i) => {
                const active = device.deviceId === devices.active[group.kind];
                return (
                  <button
                    key={device.deviceId}
                    className={`device-option${active ? " active" : ""}`}
                    onClick={() => {
                      onSelect(group.kind, device.deviceId);
                      setOpen(false);
                    }}
                  >
                    <span className="tick">
                      {active && <Icon name="check" size={14} />}
                    </span>
                    <span className="name">
                      {device.label || `${group.label} ${i + 1}`}
                    </span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReactionPicker({ onReact }: { onReact: (emoji: string) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useDismiss<HTMLDivElement>(open, () => setOpen(false));

  // Emoji are the right thing *here* — a reaction is an emoji — but they were
  // the wrong thing on the toolbar, where they were standing in for icons.
  return (
    <div className="popover-anchor" ref={ref}>
      <button className="btn icon" onClick={() => setOpen((o) => !o)} title="React" aria-label="React">
        <Icon name="smile" />
      </button>
      {open && (
        <div className="card popover reaction-menu">
          {REACTIONS.map((emoji) => (
            <button
              key={emoji}
              className="reaction-option"
              onClick={() => {
                onReact(emoji);
                setOpen(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------- chat */

function Chat({ live }: { live: ReturnType<typeof useLiveRoom> }) {
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [live.messages.length]);

  return (
    <div className="chat">
      <div className="messages">
        {live.messages.length === 0 && (
          <p className="hint" style={{ textAlign: "center" }}>
            Nothing yet. Say something.
          </p>
        )}
        {live.messages.map((m) => (
          <div key={m.id} className={`msg${m.mine ? " mine" : ""}`}>
            {!m.mine && <div className="who">{m.name}</div>}
            <div className="body">{m.text}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          live.sendChat(draft);
          setDraft("");
        }}
      >
        <input
          className="input"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Message the room"
          maxLength={2000}
        />
        <button className="btn primary" type="submit" disabled={!draft.trim()}>
          Send
        </button>
      </form>
    </div>
  );
}

/* -------------------------------------------------------------- people */

function People({
  members,
  room,
  me,
  presentIds,
  onChanged,
  setMembers,
}: {
  members: Member[];
  room: RoomType;
  me: User;
  presentIds: Set<number>;
  onChanged: () => Promise<RoomType>;
  setMembers: (m: Member[]) => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);
  const isHost = room.role === "host";
  const canModerate = isHost || room.role === "moderator";

  async function ban(userId: number) {
    if (!confirm("Remove them from the room? They won't be able to rejoin.")) return;
    setBusy(userId);
    try {
      await api.banMember(room.id, userId);
      setMembers(members.filter((m) => m.user.id !== userId));
    } catch {
      alert("Couldn't remove them.");
    } finally {
      setBusy(null);
    }
  }

  async function promote(userId: number, role: "moderator" | "member") {
    setBusy(userId);
    try {
      await api.setRole(room.id, userId, role);
      await onChanged();
      const { members: fresh } = await api.getRoom(room.id);
      setMembers(fresh);
    } catch {
      alert("Couldn't change their role.");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="tab-body">
      {members.map((m) => {
        const isMe = m.user.id === me.id;
        const here = presentIds.has(m.user.id);
        return (
          <div className="person" key={m.user.id}>
            <div className="pfp" style={here ? { borderColor: "var(--accent)" } : undefined}>
              {m.user.display_name.slice(0, 1).toUpperCase()}
            </div>
            <div className="who">
              <div className="n">
                {m.user.display_name}
                {isMe ? " (you)" : ""}
              </div>
              <div className="r">{here ? "here now" : m.role}</div>
            </div>
            {canModerate && !isMe && m.role !== "host" && (
              <div className="row">
                {isHost && (
                  <button
                    className="btn small ghost"
                    disabled={busy === m.user.id}
                    onClick={() =>
                      promote(m.user.id, m.role === "moderator" ? "member" : "moderator")
                    }
                  >
                    {m.role === "moderator" ? "Demote" : "Mod"}
                  </button>
                )}
                <button
                  className="btn small danger"
                  disabled={busy === m.user.id}
                  onClick={() => ban(m.user.id)}
                >
                  Remove
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------ requests */

function Requests({
  knocks,
  setKnocks,
  onApproved,
}: {
  knocks: Knock[];
  setKnocks: (k: Knock[]) => void;
  onApproved: () => Promise<RoomType>;
}) {
  const [busy, setBusy] = useState<number | null>(null);

  async function decide(knock: Knock, approve: boolean) {
    setBusy(knock.id);
    try {
      if (approve) {
        await api.approveKnock(knock.id);
        await onApproved();
      } else {
        await api.denyKnock(knock.id);
      }
      setKnocks(knocks.filter((k) => k.id !== knock.id));
    } catch {
      alert("Couldn't do that.");
    } finally {
      setBusy(null);
    }
  }

  if (knocks.length === 0) {
    return (
      <div className="tab-body">
        <p className="hint" style={{ textAlign: "center" }}>
          Nobody's waiting.
        </p>
      </div>
    );
  }

  return (
    <div className="tab-body">
      {knocks.map((knock) => (
        <div className="knock" key={knock.id}>
          <div className="row">
            <div className="pfp">{knock.user.display_name.slice(0, 1).toUpperCase()}</div>
            <strong>{knock.user.display_name}</strong>
          </div>
          {knock.message && <p className="msg-text">"{knock.message}"</p>}
          <div className="row">
            <button
              className="btn small primary"
              disabled={busy === knock.id}
              onClick={() => decide(knock, true)}
            >
              Let in
            </button>
            <button
              className="btn small ghost"
              disabled={busy === knock.id}
              onClick={() => decide(knock, false)}
            >
              Not now
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------ settings */

function Settings({ room, setRoom }: { room: RoomType; setRoom: (r: RoomType) => void }) {
  const [codes, setCodes] = useState<JoinCode[]>([]);
  const [justMade, setJustMade] = useState<number | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const isHost = room.role === "host";

  const loadCodes = useCallback(() => {
    api
      .listCodes(room.id)
      .then((r) => setCodes(r.codes))
      .catch(() => setCodes([]));
  }, [room.id]);

  useEffect(loadCodes, [loadCodes]);

  async function setTheme(preset: ThemeName) {
    const { room: updated } = await api.updateRoom(room.id, { theme: { preset } });
    setRoom(updated);
  }

  async function setAccess(access_mode: AccessMode) {
    const { room: updated } = await api.updateRoom(room.id, { access_mode });
    setRoom(updated);
  }

  async function newCode() {
    setBusy(true);
    try {
      const { code } = await api.createCode(room.id);
      setJustMade(code.id);
      loadCodes();
    } finally {
      setBusy(false);
    }
  }

  // Copies the code itself rather than a URL. The join box takes a code, and a
  // link would have to name an origin this build cannot know it is served from
  // once a custom domain is in front of it.
  async function copy(code: string) {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(code);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      // Clipboard access can be refused; the code is on screen to read anyway.
    }
  }

  async function revoke(id: number) {
    if (!confirm("Revoke this code? Anyone still using it will be locked out.")) return;
    await api.revokeCode(id);
    loadCodes();
  }

  const active = themeFrom(room.theme).name;

  return (
    <div className="tab-body">
      <h4 style={{ margin: "0 0 10px", fontSize: 13, color: "var(--text-dim)" }}>
        ROOM COLOURS
      </h4>
      <div className="theme-row">
        {Object.values(THEMES).map((t) => (
          <button
            key={t.name}
            className={`theme-swatch${active === t.name ? " active" : ""}`}
            style={{ background: t.swatch }}
            title={t.label}
            onClick={() => setTheme(t.name)}
          />
        ))}
      </div>

      {isHost && (
        <>
          <h4 style={{ margin: "20px 0 10px", fontSize: 13, color: "var(--text-dim)" }}>
            WHO CAN GET IN
          </h4>
          <div className="seg">
            <button
              className={room.access_mode === "open" ? "active" : ""}
              onClick={() => setAccess("open")}
            >
              Open
            </button>
            <button
              className={room.access_mode === "closed" ? "active" : ""}
              onClick={() => setAccess("closed")}
            >
              Ask first
            </button>
          </div>
        </>
      )}

      <h4 style={{ margin: "22px 0 10px", fontSize: 13, color: "var(--text-dim)" }}>
        INVITE CODES
      </h4>

      {codes.length === 0 && (
        <p className="hint" style={{ marginBottom: 12 }}>
          No active codes. Make one to let anyone else in.
        </p>
      )}

      {codes.map((code) => (
        <div className="code-row" key={code.id}>
          <div style={{ minWidth: 0 }}>
            {code.code ? (
              <div className="val code-plain">{code.code}</div>
            ) : (
              <div className="val">{code.label || "Code"}</div>
            )}
            <div className="hint" style={{ fontSize: 12 }}>
              {code.code ? "" : "can't be shown — "}used {code.use_count}
              {code.max_uses ? ` / ${code.max_uses}` : ""}
              {code.id === justMade ? " · new" : ""}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
            {code.code && (
              <button
                className="btn small"
                onClick={() => copy(code.code!)}
                title="Copy the join link"
              >
                {copied === code.code ? "Copied" : "Copy"}
              </button>
            )}
            <button className="btn small danger" onClick={() => revoke(code.id)}>
              Revoke
            </button>
          </div>
        </div>
      ))}

      <button
        className="btn"
        onClick={newCode}
        disabled={busy}
        style={{ marginTop: 14, width: "100%" }}
      >
        {busy ? <span className="spinner" /> : "New code"}
      </button>
    </div>
  );
}
